import { CollectionOperation, Storage } from '@wix/wix-data-schema-types'
import type { EDITOR_TYPE } from '@wix/wix-data-client-common'
import {
  getDeveloperMode,
  isBlocks,
  digestSchema,
  isExternalCollection,
  isOldFormsCollection,
  schemaSupportsDPs,
} from '@wix/wix-data-client-common'
import { MULTILINGUAL } from '@wix/app-definition-ids'
import { omit } from 'lodash'
import {
  type EditorSDK,
  type TranslateFunc,
} from '@wix/wix-data-client-common-standalone'
import type { Schema } from '@wix/wix-data-schema-types'
import type Experiments from '@wix/wix-experiments'

const ACTION_NAMES = {
  OPEN_COLLECTION: 'OPEN_COLLECTION',
  VIEW_PERMISSIONS: 'VIEW_PERMISSIONS',
  EDIT_SETTINGS: 'EDIT_SETTINGS',
  PERMISSIONS_AND_PRIVACY: 'PERMISSIONS_AND_PRIVACY',
  MANAGE_TRANSLATIONS: 'MANAGE_TRANSLATIONS',
  ADD_BLANK_DYNAMIC_PAGE: 'ADD_BLANK_DYNAMIC_PAGE',
  ADD_DYNAMIC_ITEM_PAGE: 'ADD_DYNAMIC_ITEM_PAGE',
  ADD_DYNAMIC_LIST_PAGE: 'ADD_DYNAMIC_LIST_PAGE',
  ADD_NEW_DYNAMIC_PAGE: 'ADD_NEW_DYNAMIC_PAGE',
  ADD_REMOVE_HOOKS: 'ADD_REMOVE_HOOKS',
  DUPLICATE_COLLECTION: 'DUPLICATE_COLLECTION',
  REMOVE_COLLECTION: 'REMOVE_COLLECTION',
} as const

type ActionName = keyof typeof ACTION_NAMES
export type Action = (typeof ACTION_NAMES)[ActionName]

const getContextMenuActions = ({ t }: { t: TranslateFunc }) => {
  const addDynamicPageActions = {
    [ACTION_NAMES.ADD_NEW_DYNAMIC_PAGE]: {
      title: t('Collections_Tree_Context_Menu_Actions_Add_Dynamic_Page'),
      event: 'openNewDynamicPagePanel',
      icon: 'page_dynamic_icon',
    },
  }

  return {
    [ACTION_NAMES.OPEN_COLLECTION]: {
      title: t('Collections_Tree_Context_Menu_Actions_Open'),
      icon: 'corvid_tree__collection',
      event: 'openCollectionClicked',
    },
    ...addDynamicPageActions,
    [ACTION_NAMES.MANAGE_TRANSLATIONS]: {
      title: t('Collections_Tree_Context_Menu_Actions_Manage_Translations'),
      event: 'manageTranslations',
      icon: 'manage_translations',
    },
    [ACTION_NAMES.VIEW_PERMISSIONS]: {
      title: t('WixCode_TreeView_FileTree_QuickActions_UpdatePermissions'),
      icon: 'contexMenuActionEditPermissions',
      event: 'openCollectionPermissionsClicked',
    },
    [ACTION_NAMES.EDIT_SETTINGS]: {
      title: t(
        'WixCode_TreeView_FileTree_QuickActions_Edit_Collection_Settings',
      ),
      icon: 'editCollection',
      event: 'editCollectionClicked',
    },
    [ACTION_NAMES.PERMISSIONS_AND_PRIVACY]: {
      title: t(
        'WixCode_TreeView_FileTree_QuickActions_Permissions_And_Privacy',
      ),
      icon: 'contexMenuActionEditPermissions',
      event: 'permissionsAndPrivacyClicked',
    },
    [ACTION_NAMES.ADD_REMOVE_HOOKS]: {
      title: t('Collections_Tree_Context_Menu_Actions_AddRemoveHooks'),
      event: 'addRemoveHooksClicked',
      icon: 'add_hook_icon',
    },
    [ACTION_NAMES.DUPLICATE_COLLECTION]: {
      title: t('Collections_Tree_Context_Menu_Actions_Duplicate'),
      event: 'duplicateCollectionClicked',
      icon: 'duplicate_icon',
    },
    [ACTION_NAMES.REMOVE_COLLECTION]: {
      title: t('Collections_Tree_Context_Menu_Actions_Remove'),
      event: 'removeCollectionClicked',
      icon: 'delete_icon',
    },
  }
}

const isActionAllowedForCollection = ({
  action,
  isUserCollection,
  isEditableExternalDb,
  isSharedCollection,
  hasPermissionToAddPages,
  hasPermissionToManageContent,
  isDeveloperModeOn,
  shouldShowHooksIfInReadOnlyMode,
  isDPCreationSupportedBySchema,
  isInBlocks,
  shouldShowManageTranslations,
  isRemovableFormsCollection,
}: {
  action: Action
  isUserCollection: boolean
  isEditableExternalDb: boolean
  isSharedCollection: boolean
  hasPermissionToAddPages: boolean
  hasPermissionToManageContent: boolean
  isDeveloperModeOn: boolean
  shouldShowHooksIfInReadOnlyMode: boolean
  isDPCreationSupportedBySchema: boolean
  isInBlocks: boolean
  shouldShowManageTranslations: boolean
  isRemovableFormsCollection: boolean
}): boolean => {
  const shouldShowPageCreationOption =
    hasPermissionToAddPages && isDPCreationSupportedBySchema && !isInBlocks

  const canWriteToSchema =
    (isUserCollection || isEditableExternalDb) &&
    !isSharedCollection &&
    hasPermissionToManageContent

  switch (action) {
    case ACTION_NAMES.OPEN_COLLECTION:
      return true
    case ACTION_NAMES.VIEW_PERMISSIONS:
      return !canWriteToSchema
    case ACTION_NAMES.EDIT_SETTINGS:
    case ACTION_NAMES.PERMISSIONS_AND_PRIVACY:
      return canWriteToSchema
    case ACTION_NAMES.DUPLICATE_COLLECTION:
      return (
        isUserCollection && !isSharedCollection && hasPermissionToManageContent
      )
    case ACTION_NAMES.REMOVE_COLLECTION:
      return canWriteToSchema || isRemovableFormsCollection
    case ACTION_NAMES.ADD_DYNAMIC_ITEM_PAGE:
    case ACTION_NAMES.ADD_BLANK_DYNAMIC_PAGE:
    case ACTION_NAMES.ADD_DYNAMIC_LIST_PAGE:
    case ACTION_NAMES.ADD_NEW_DYNAMIC_PAGE:
      return shouldShowPageCreationOption
    case ACTION_NAMES.ADD_REMOVE_HOOKS:
      return (
        hasPermissionToManageContent &&
        isDeveloperModeOn &&
        shouldShowHooksIfInReadOnlyMode &&
        !isInBlocks &&
        !isSharedCollection
      )
    case ACTION_NAMES.MANAGE_TRANSLATIONS:
      return (
        /**
         * Translations in Blocks are temporarily disabled until CMS related Multilingual issues are resolved
         * https://jira.wixpress.com/browse/WBL-8218
         */
        !isInBlocks &&
        shouldShowManageTranslations &&
        isUserCollection &&
        !isSharedCollection
      )
  }
}

export const updateAppStateActions = ({
  editorSdkProxy,
  t,
}: {
  t: TranslateFunc
  editorSdkProxy: EditorSDK
}) => {
  editorSdkProxy.editor.updateAppState({
    actions: getContextMenuActions({ t }),
  })
}

export const getActionsBySchema = async ({
  editorSdkProxy,
  schemas,
  editorType,
}: {
  editorSdkProxy: EditorSDK
  editorType: EDITOR_TYPE
  schemas: Schema[]
  experimentsManager: Experiments
}): Promise<Record<string, Action[]>> => {
  const hasPermissionToAddPages =
    await editorSdkProxy.document.permissions.isCustomPermissionsGranted({
      permissions: ['DOCUMENT.EDIT_STRUCTURE', 'WIX_DATA.UPDATE_SCHEMA'],
    })

  const hasPermissionToManageContent =
    await editorSdkProxy.document.permissions.isCustomPermissionsGranted({
      permissions: ['WIX_DATA.UPDATE_SCHEMA'],
    })

  const isDeveloperModeOn = await getDeveloperMode(editorSdkProxy)

  const isInBlocks = isBlocks(editorType)

  const isReadOnly = async () => {
    try {
      return await editorSdkProxy.editor.developerMode.isReadOnly()
    } catch {
      return false
    }
  }

  const shouldShowHooksIfInReadOnlyMode = !(await isReadOnly())

  const isMultilingualInstalled = async () => {
    try {
      return editorSdkProxy.document.application.isApplicationInstalled({
        appDefinitionId: MULTILINGUAL,
      })
    } catch {
      return false
    }
  }

  const hasPermissionToManageTranslations = () =>
    editorSdkProxy.document.permissions.isCustomPermissionsGranted({
      permissions: [
        'SIDEBAR.ENABLE-MULTILINGUAL-SETTINGS',
        'SIDEBAR.VIEW-MULTILINGUAL-SETTINGS',
      ],
    })

  const shouldShowManageTranslations =
    (await isMultilingualInstalled()) &&
    (await hasPermissionToManageTranslations())

  return schemas.reduce(
    (collectionsTypes, schema) => {
      const { namespace, plugins, id, collectionOperations } = digestSchema({
        rawSchema: schema,
      })

      const isEditableExternalDb = Boolean(
        isExternalCollection(schema) &&
        // editable external schema should have both operations to enable additional actions
        collectionOperations?.includes(CollectionOperation.UPDATE) &&
        collectionOperations?.includes(CollectionOperation.REMOVE),
      )

      const isUserCollection =
        !namespace || (schema.storage === Storage.staticAppSchema && isInBlocks)

      const isSharedCollection = !!plugins?.shared

      const isDPCreationSupportedBySchema = schemaSupportsDPs(schema)

      const isRemovableFormsCollection = isOldFormsCollection(schema)

      const keysToRemove: Action[] = [
        ACTION_NAMES.ADD_BLANK_DYNAMIC_PAGE,
        ACTION_NAMES.ADD_DYNAMIC_ITEM_PAGE,
        ACTION_NAMES.ADD_DYNAMIC_LIST_PAGE,
      ]

      const actions: Action[] = Object.values(omit(ACTION_NAMES, keysToRemove))

      collectionsTypes[id] = actions.filter(action =>
        isActionAllowedForCollection({
          action,
          isUserCollection,
          isEditableExternalDb,
          isSharedCollection,
          hasPermissionToAddPages,
          hasPermissionToManageContent,
          isDeveloperModeOn,
          shouldShowHooksIfInReadOnlyMode,
          isDPCreationSupportedBySchema,
          isInBlocks,
          shouldShowManageTranslations,
          isRemovableFormsCollection,
        }),
      )

      return collectionsTypes
    },
    {} as Record<string, Action[]>,
  )
}
